import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import { fetchData, updateData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import { CircularProgress, Fade, Tooltip, Chip, LinearProgress, Select, MenuItem, FormControl, Collapse, IconButton } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import BranchCreateModal from './BranchCreateModal';
import BranchEditModal from './BranchEditModal';

// Helper function to display field values with placeholder for null/empty
const displayValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'Not specified';
    }
    return value;
};

// Memoized BranchDetailCard component for better performance
const BranchDetailCard = memo(({ branch }) => {
    return (
        <div style={{
            background: '#f8f9fa',
            padding: '16px',
            borderTop: '1px solid #e9ecef'
        }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
            }}>
                {/* Contact Information Section */}
                <div style={{
                    background: 'white',
                    borderRadius: '6px',
                    padding: '10px',
                    border: '1px solid #e9ecef'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '8px',
                        paddingBottom: '6px',
                        borderBottom: '1px solid #e9ecef'
                    }}>
                        <PhoneIcon style={{ fontSize: '14px', color: '#667eea' }} />
                        <span style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            color: '#2d3748',
                            textTransform: 'uppercase'
                        }}>Contact Information</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>Primary Contact</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#2d3748' }}>
                                {displayValue(branch.ContactNumber1)}
                            </p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>Secondary Contact</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#2d3748' }}>
                                {displayValue(branch.ContactNumber2)}
                            </p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>Email</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#2d3748' }}>
                                {displayValue(branch.Email)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Address Details Section */}
                <div style={{
                    background: 'white',
                    borderRadius: '6px',
                    padding: '10px',
                    border: '1px solid #e9ecef'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '8px',
                        paddingBottom: '6px',
                        borderBottom: '1px solid #e9ecef'
                    }}>
                        <LocationOnIcon style={{ fontSize: '14px', color: '#667eea' }} />
                        <span style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            color: '#2d3748',
                            textTransform: 'uppercase'
                        }}>Address Details</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>Address</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#2d3748' }}>
                                {displayValue(branch.AddressLine1)}
                            </p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>Location</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#2d3748' }}>
                                {displayValue(branch.Location)}
                            </p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>Pin Code</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#2d3748' }}>
                                {displayValue(branch.PinCode)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tax Information Section */}
                <div style={{
                    background: 'white',
                    borderRadius: '6px',
                    padding: '10px',
                    border: '1px solid #e9ecef'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '8px',
                        paddingBottom: '6px',
                        borderBottom: '1px solid #e9ecef'
                    }}>
                        <ReceiptIcon style={{ fontSize: '14px', color: '#667eea' }} />
                        <span style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            color: '#2d3748',
                            textTransform: 'uppercase'
                        }}>Tax Information</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>GST Number</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#2d3748', fontFamily: 'monospace' }}>
                                {displayValue(branch.GSTNumber)}
                            </p>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>PAN Number</span>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: '500', color: '#2d3748', fontFamily: 'monospace' }}>
                                {displayValue(branch.PanNumber)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Configuration Settings Section */}
                <div style={{
                    background: 'white',
                    borderRadius: '6px',
                    padding: '10px',
                    border: '1px solid #e9ecef'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '8px',
                        paddingBottom: '6px',
                        borderBottom: '1px solid #e9ecef'
                    }}>
                        <SettingsIcon style={{ fontSize: '14px', color: '#667eea' }} />
                        <span style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            color: '#2d3748',
                            textTransform: 'uppercase'
                        }}>Configuration</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>GST Enabled</span>
                            <div style={{ marginTop: '4px' }}>
                                <Chip
                                    label={branch.IsGST ? 'Yes' : 'No'}
                                    size="small"
                                    style={{
                                        height: '18px',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        background: branch.IsGST
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                            : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                                        color: 'white'
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const CompanyEdit = ({ match }) => {
    // Session validation
    useSessionValidation();
    
    const { isAuthenticated, companyId, logout, token, userId } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [openBranchModal, setOpenBranchModal] = useState(false);
    const [openBranchEditModal, setOpenBranchEditModal] = useState(false);
    const [selectedBranchForEdit, setSelectedBranchForEdit] = useState(null);
    const [isSavingBranch, setIsSavingBranch] = useState(false);
    const [expandedBranchId, setExpandedBranchId] = useState(null);

    // Toggle branch expansion - collapse if same branch clicked, expand new one otherwise
    const handleBranchRowClick = (branchId) => {
        setExpandedBranchId(prevId => prevId === branchId ? null : branchId);
    };

    useEffect(() => {
        setMounted(true);
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

    }, [isAuthenticated, companyId]);

    const [formData, setFormData] = useState([]);
    const [branches, setBranches] = useState([]);

    // Fetch company data only once
    useEffect(() => {
        if (!companyId || !token) return;
        
        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Company Details:', error);
            });
    }, [companyId, token]);

    // Fetch branch list only once
    useEffect(() => {
        if (!companyId || !token) return;
        
        fetchData('BranchApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    setBranches(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Branch Details:', error);
            });
    }, [companyId, token]);

    // Memoized change handler
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value === 'true' ? true : e.target.type === 'checkbox' ? e.target.checked : value,
        }));
    }, []);

    // Memoized progress calculation
    const formProgress = useMemo(() => {
        const requiredFields = ['Name', 'ContactNumber1', 'AddressLine1', 'Location'];
        const optionalFields = ['GSTNumber', 'ContactNumber2', 'ContactNumber3', 'Email', 'AddressLine2', 'PostalCode', 'CashOpening', 'FreightGSTRate'];

        let filledRequired = 0;
        let filledOptional = 0;

        requiredFields.forEach(field => {
            if (formData[field] && formData[field].toString().trim() !== '') {
                filledRequired++;
            }
        });

        optionalFields.forEach(field => {
            if (formData[field] && formData[field].toString().trim() !== '') {
                filledOptional++;
            }
        });

        const requiredWeight = 70;
        const optionalWeight = 30;

        const progress = (filledRequired / requiredFields.length) * requiredWeight +
            (filledOptional / optionalFields.length) * optionalWeight;

        return Math.round(progress);
    }, [formData]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();
        setIsLoading(true);

        const updatedData = {
            ...formData,
            UpdatedBy: userId,
        };

        updateData('CompanyApi', `${companyId}`, updatedData, token)
            .then((response) => {
                console.log('Record updated successfully:', response.data);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setIsLoading(false);
            });
    }, [formData, userId, companyId, token]);

    const handleOpenBranchModal = useCallback(() => {
        setOpenBranchModal(true);
    }, []);

    const handleCloseBranchModal = useCallback(() => {
        setOpenBranchModal(false);
    }, []);

    const handleOpenBranchEditModal = useCallback((branch) => {
        setSelectedBranchForEdit(branch);
        setOpenBranchEditModal(true);
    }, []);

    const handleCloseBranchEditModal = useCallback(() => {
        setOpenBranchEditModal(false);
        setSelectedBranchForEdit(null);
    }, []);

    const handleSaveBranch = useCallback(async (branchData) => {
        setIsSavingBranch(true);
        try {
            const apiData = {
                CompanyId: companyId,
                Code: branchData.Code,
                Name: branchData.Name,
                AddressLine1: branchData.AddressLine1,
                AddressLine2: branchData.AddressLine2,
                ContactNumber1: branchData.ContactNumber1,
                ContactNumber2: branchData.ContactNumber2,
                ContactNumber3: branchData.ContactNumber3,
                Email: branchData.Email,
                GSTNumber: branchData.GSTNumber,
                PanNumber: branchData.PanNumber,
                Location: branchData.Location,
                PinCode: branchData.PinCode,
                StateCodeId: branchData.StateCodeId ? parseInt(branchData.StateCodeId) : null,
                PaymentBankId: branchData.PaymentBankId ? parseInt(branchData.PaymentBankId) : null,
                BToBPrefix: branchData.BToBPrefix,
                BToCPrefix: branchData.BToCPrefix,
                IGSTPrefix: branchData.IGSTPrefix,
                IGSTCustomerPrefix: branchData.IGSTCustomerPrefix,
                CashOpeningBalance: branchData.CashOpeningBalance ? parseFloat(branchData.CashOpeningBalance) : 0,
                CashOpeningDate: branchData.CashOpeningDate || null,
                StockOpeningBalance: branchData.StockOpeningBalance ? parseFloat(branchData.StockOpeningBalance) : 0,
                StockOpeningDate: branchData.StockOpeningDate || null,
                IsGST: branchData.IsGST || false,
                CreatedBy: userId,
            };

            const response = await postData('BranchApi', apiData, token);
            if (response.responseCode === '1111') {
                setBranches(prevBranches => [...prevBranches, {
                    id: response.response_data.Id || Date.now(),
                    code: apiData.Code,
                    name: apiData.Name,
                    contact: apiData.ContactNumber1,
                    status: 'Active'
                }]);
                handleCloseBranchModal();
            } else {
                console.error('Error creating branch:', response.message);
            }
        } catch (error) {
            console.error('Error creating branch:', error);
        } finally {
            setIsSavingBranch(false);
        }
    }, [companyId, userId, token, handleCloseBranchModal]);

    const handleUpdateBranch = useCallback(async (branchData) => {
        setIsSavingBranch(true);
        try {
            const apiData = {
                Id: branchData.Id,
                CompanyId: companyId,
                Code: branchData.Code,
                Name: branchData.Name,
                AddressLine1: branchData.AddressLine1,
                AddressLine2: branchData.AddressLine2,
                ContactNumber1: branchData.ContactNumber1,
                ContactNumber2: branchData.ContactNumber2,
                ContactNumber3: branchData.ContactNumber3,
                Email: branchData.Email,
                GSTNumber: branchData.GSTNumber,
                PanNumber: branchData.PanNumber,
                Location: branchData.Location,
                PinCode: branchData.PinCode,
                StateCodeId: branchData.StateCodeId ? parseInt(branchData.StateCodeId) : null,
                PaymentBankId: branchData.PaymentBankId ? parseInt(branchData.PaymentBankId) : null,
                BToBPrefix: branchData.BToBPrefix,
                BToCPrefix: branchData.BToCPrefix,
                IGSTPrefix: branchData.IGSTPrefix,
                IGSTCustomerPrefix: branchData.IGSTCustomerPrefix,
                CashOpeningBalance: branchData.CashOpeningBalance ? parseFloat(branchData.CashOpeningBalance) : 0,
                CashOpeningDate: branchData.CashOpeningDate || null,
                StockOpeningBalance: branchData.StockOpeningBalance ? parseFloat(branchData.StockOpeningBalance) : 0,
                StockOpeningDate: branchData.StockOpeningDate || null,
                IsGST: branchData.IsGST || false,
                ModifiedBy: userId,
                IsActive: true,
                CreatedBy: branchData.CreatedBy,
                CreatedDate: branchData.CreatedDate,
            };

            const response = await updateData('BranchApi', branchData.Id, apiData, token);
            if (response.responseCode === '1111') {
                setBranches(prevBranches =>
                    prevBranches.map(branch =>
                        branch.Id === branchData.Id
                            ? { ...branch, ...apiData }
                            : branch
                    )
                );
                handleCloseBranchEditModal();
                
                // Refresh branch list
                const refreshResponse = await fetchData('BranchApi', `/${companyId}`, token);
                if (refreshResponse.responseCode == '1111') {
                    setBranches(refreshResponse.response_data);
                }
            } else {
                console.error('Error updating branch:', response.message);
            }
        } catch (error) {
            console.error('Error updating branch:', error);
        } finally {
            setIsSavingBranch(false);
        }
    }, [companyId, userId, token, handleCloseBranchEditModal]);

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{ padding: '0 8px' }}>
                {/* Background Pattern */}
                <div style={{
                    position: 'absolute',
                    top: 15,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundSize: '400px 400px, 400px 400px, 20px 20px',
                    pointerEvents: 'none'
                }} />

                <div className="executive-card" style={{
                    maxWidth: '100%',
                    margin: '0',
                    borderRadius: '8px',
                    padding: '0',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Enhanced Progress Bar */}
                    <div style={{
                        height: '2px',
                        background: 'linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 100%)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            height: '100%',
                            width: `${formProgress}%`,
                            background: formProgress === 100
                                ? 'linear-gradient(90deg, #10b981 0%, #059669 50%, #047857 100%)'
                                : 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
                            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            boxShadow: formProgress > 0 ? '0 0 20px rgba(59, 130, 246, 0.4)' : 'none'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                animation: formProgress > 0 ? 'shimmerEffect 2s infinite' : 'none'
                            }} />
                        </div>
                    </div>

                    {/* Header Section */}
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '12px 16px',
                        borderRadius: '12px 12px 0 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <BusinessIcon style={{ fontSize: '24px', color: 'white' }} />
                            <div>
                                <h4 style={{
                                    fontSize: '17px',
                                    fontWeight: '700',
                                    margin: 0,
                                    color: 'white',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Company Management
                                </h4>
                                <p style={{
                                    margin: '4px 0 0 0',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: '12px',
                                    fontWeight: '400'
                                }}>
                                    Configure company and branch settings
                                </p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <Tooltip title="Save changes" arrow>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    startIcon={isLoading ? null : <SaveIcon />}
                                    style={{
                                        background: isLoading ? 'rgba(255, 255, 255, 0.3)' : 'white',
                                        color: isLoading ? 'rgba(255, 255, 255, 0.7)' : '#667eea',
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        minHeight: '36px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {isLoading ? <CircularProgress size={16} color="inherit" /> : <span className="btn-text">Save Changes</span>}
                                </Button>
                            </Tooltip>
                            <Tooltip title="Return to dashboard" arrow>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to="/Home"
                                    startIcon={<ArrowBackIcon />}
                                    style={{
                                        borderColor: 'white',
                                        color: 'white',
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        minHeight: '36px',
                                        textTransform: 'none'
                                    }}
                                >
                                    <span className="btn-text">Back</span>
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    <style>{`
                    .premium-input {
                        transition: all 0.3s ease;
                        border: 1.5px solid #e5e7eb;
                        background: #ffffff;
                    }
                    
                    .premium-input:focus {
                        border-color: #667eea;
                        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                        outline: none;
                    }
                    
                    .premium-input:hover:not(:focus) {
                        border-color: #9ca3af;
                    }
                    
                    .status-badge {
                        display: inline-flex;
                        align-items: 'center',
                        padding: 4px 12px;
                        borderRadius: 20px;
                        fontSize: 11px;
                        fontWeight: 600;
                        textTransform: uppercase;
                        letterSpacing: 0.5px;
                        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                        color: white;
                        boxShadow: 0 2px 4px rgba(16, 185, 129, 0.2);
                    }
                    
                    @media (max-width: 480px) {
                        .btn-text {
                            display: none !important;
                        }
                    }
                `}</style>

                    <div style={{
                        background: 'white',
                        padding: '16px',
                        borderRadius: '0 0 12px 12px'
                    }}>
                        {/* Progress Indicator */}
                        <div style={{
                            marginBottom: '16px',
                            padding: '12px',
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                            }}>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#667eea' }}>
                                    Form Completion
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#667eea' }}>
                                    {formProgress}%
                                </span>
                            </div>
                            <LinearProgress
                                variant="determinate"
                                value={formProgress}
                                style={{
                                    height: '6px',
                                    borderRadius: '3px',
                                    background: '#e0e0e0'
                                }}
                                sx={{
                                    '& .MuiLinearProgress-bar': {
                                        background: formProgress === 100
                                            ? 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)'
                                            : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                        borderRadius: '3px'
                                    }
                                }}
                            />
                        </div>

                        {/* Two Column Layout */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '16px'
                        }}>
                            {/* Left Column - Company Configuration */}
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    padding: '10px 12px',
                                    borderRadius: '8px 8px 0 0',
                                    marginBottom: '0'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <BusinessIcon style={{ fontSize: '18px', color: 'white' }} />
                                        <h6 style={{
                                            margin: 0,
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            color: 'white',
                                            textTransform: 'uppercase'
                                        }}>
                                            Company Configuration
                                        </h6>
                                    </div>
                                </div>
                                <div style={{
                                    padding: '12px',
                                    borderRadius: '0 0 8px 8px',
                                    border: '1px solid #e9ecef',
                                    borderTop: 'none',
                                    background: '#f8f9fa'
                                }}>
                                    {/* Company Name */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            color: '#2d3748',
                                            marginBottom: '6px'
                                        }}>
                                            Company Name
                                        </label>
                                        <input
                                            className="premium-input"
                                            name='Name'
                                            value={formData.Name}
                                            onChange={handleChange}
                                            type="text"
                                            placeholder="Enter your company name"
                                            required
                                            maxLength={50}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                fontSize: '13px',
                                                borderRadius: '6px',
                                                fontWeight: '500',
                                                color: '#2d3748'
                                            }}
                                        />
                                    </div>

                                    {/* Sales Calculation Type */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            color: '#2d3748',
                                            marginBottom: '6px'
                                        }}>
                                            Sales Calculation Type
                                        </label>
                                        <FormControl fullWidth>
                                            <Select
                                                name="SalesCalculationType"
                                                value={formData.SalesCalculationType || 1}
                                                onChange={handleChange}
                                                className="premium-input"
                                                style={{
                                                    background: 'white',
                                                    fontSize: '13px',
                                                    height: '38px',
                                                    borderRadius: '6px',
                                                    fontWeight: '500',
                                                    color: '#2d3748'
                                                }}
                                            >
                                                <MenuItem value={1} style={{ fontSize: '13px', fontWeight: '500' }}>
                                                    1. For Inclusive GST
                                                </MenuItem>
                                                <MenuItem value={2} style={{ fontSize: '13px', fontWeight: '500' }}>
                                                    2. For Exclusive GST
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Branch Management */}
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    padding: '10px 12px',
                                    borderRadius: '8px 8px 0 0',
                                    marginBottom: '0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <AccountTreeIcon style={{ fontSize: '18px', color: 'white' }} />
                                        <h6 style={{
                                            margin: 0,
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            color: 'white',
                                            textTransform: 'uppercase'
                                        }}>
                                            Branch Network
                                        </h6>
                                    </div>
                                </div>
                                <div style={{
                                    padding: '12px',
                                    borderRadius: '0 0 8px 8px',
                                    border: '1px solid #e9ecef',
                                    borderTop: 'none',
                                    background: '#f8f9fa'
                                }}>
                                    {/* Add New Branch Button */}
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleOpenBranchModal}
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            marginBottom: '12px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            padding: '8px 16px',
                                            textTransform: 'none',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                            width: '100%'
                                        }}
                                    >
                                        Add New Branch
                                    </Button>

                                    {/* Branch List */}
                                    <div style={{
                                        background: 'white',
                                        borderRadius: '8px',
                                        border: '1px solid #e9ecef',
                                        overflow: 'hidden'
                                    }}>
                                        {branches.length === 0 ? (
                                            <div style={{
                                                padding: '20px',
                                                textAlign: 'center',
                                                color: '#718096',
                                                fontSize: '13px'
                                            }}>
                                                No branches added yet
                                            </div>
                                        ) : (
                                            branches.map((branch, index) => (
                                                <div key={branch.Id}>
                                                    <div
                                                        onClick={() => handleBranchRowClick(branch.Id)}
                                                        style={{
                                                            padding: '12px',
                                                            cursor: 'pointer',
                                                            borderBottom: index < branches.length - 1 ? '1px solid #e9ecef' : 'none',
                                                            background: expandedBranchId === branch.Id ? '#f8f9fa' : 'white',
                                                            transition: 'background 0.2s ease'
                                                        }}
                                                    >
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                    fontSize: '13px',
                                                                    fontWeight: '600',
                                                                    color: '#2d3748',
                                                                    marginBottom: '4px'
                                                                }}>
                                                                    {branch.Name}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '11px',
                                                                    color: '#718096'
                                                                }}>
                                                                    {branch.Code} • {branch.ContactNumber1}
                                                                </div>
                                                            </div>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px'
                                                            }}>
                                                                <span style={{
                                                                    padding: '4px 8px',
                                                                    borderRadius: '12px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '600',
                                                                    background: branch.IsActive
                                                                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                                                        : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                                    color: 'white'
                                                                }}>
                                                                    {branch.IsActive ? 'Active' : 'Inactive'}
                                                                </span>
                                                                <Tooltip title="Edit Branch" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleOpenBranchEditModal(branch);
                                                                        }}
                                                                        style={{
                                                                            color: '#667eea',
                                                                            padding: '4px'
                                                                        }}
                                                                    >
                                                                        <EditIcon style={{ fontSize: '16px' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {expandedBranchId === branch.Id ? (
                                                                    <ExpandLessIcon style={{ fontSize: '20px', color: '#667eea' }} />
                                                                ) : (
                                                                    <ExpandMoreIcon style={{ fontSize: '20px', color: '#718096' }} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Collapse in={expandedBranchId === branch.Id} timeout={300}>
                                                        <BranchDetailCard branch={branch} />
                                                    </Collapse>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    fontFamily: 'serif',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '13px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    zIndex: 1000
                }}>
                    <span style={{ fontSize: '18px' }}>©️</span>
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>

                {/* New Branch Modal - Simplified Design */}
                <BranchCreateModal
                    open={openBranchModal}
                    onClose={handleCloseBranchModal}
                    onSave={handleSaveBranch}
                    isSaving={isSavingBranch}
                />

                {/* Edit Branch Modal */}
                <BranchEditModal
                    open={openBranchEditModal}
                    onClose={handleCloseBranchEditModal}
                    onSave={handleUpdateBranch}
                    isSaving={isSavingBranch}
                    branchData={selectedBranchForEdit}
                />
            </div>
        </Fade>
    );
};

export default CompanyEdit;
